import {
  ColorSwatchOption,
  ColorSwatchPicker,
} from "@shared/components/untitledui/base/color-swatch-picker/color-swatch-picker"
import type { UnifiedCardStack } from "@shared/models/card-stack"
import { CourseColor } from "@shared/models/colors"
import { COURSE_COLOR_MAP } from "@shared/util/colors"
import { tracked } from "@supergrain/kernel/react"
import { parseColor } from "react-aria-components"

/** The value the "No Color" option carries — transparent matches no course color. */
const NO_COLOR = "rgba(0, 0, 0, 0)"

// The enum's order is the legacy 4x4 grid's order (color, its two muted neighbors, the
// next color), which the picker's four columns reproduce row by row.
const COLORS = Object.values(CourseColor)

const sameColor = (a: string, b: string) =>
  parseColor(a).toString("rgba") === parseColor(b).toString("rgba")

/**
 * "Unit Color" — the drill-down replacing the Ember `unit/color-selector` dropdown: the
 * course palette as a 4x4 grid of swatches over a full-width "No Color" row.
 *
 * Takes the unit rather than its color: the read happens in this tracked render, so a
 * recolor re-renders the grid. Read in the menu's row table instead, it would be evaluated
 * from a callback React runs outside any tracked render, and track nothing.
 */
export const ChangeUnitColorView = tracked(function ChangeUnitColorView({
  unit,
  onSelect,
}: {
  unit: UnifiedCardStack & { attributes: { usedAs: "unit" } }
  /** Called with the chosen color, or null for "No Color". */
  onSelect: (color: CourseColor | null) => void
}) {
  const currentColor = unit.attributes.color
  const selected = currentColor ? COURSE_COLOR_MAP[currentColor] : NO_COLOR

  return (
    /* Color Grid */
    <div className="flex justify-center px-5 pt-2 pb-5">
      <ColorSwatchPicker
        aria-label="Unit color options"
        className="grid-cols-[repeat(4,auto)]"
        value={selected}
        onChange={(color) => {
          const picked = color.toString("rgba")
          if (sameColor(NO_COLOR, picked)) {
            onSelect(null)
            return
          }
          const match = COLORS.find((name) => sameColor(COURSE_COLOR_MAP[name], picked))
          if (match) onSelect(match)
        }}
      >
        {COLORS.map((name) => (
          <ColorSwatchOption
            key={name}
            color={COURSE_COLOR_MAP[name]}
            ariaLabel={name}
            className="size-11 p-1 selected:bg-transparent selected:ring-2 selected:ring-gray-700"
          >
            {/* Color Chip */}
            <span
              data-test="color swatch"
              data-color={name}
              className="size-full rounded ring-1 ring-black/10"
              style={{ backgroundColor: COURSE_COLOR_MAP[name] }}
            />
          </ColorSwatchOption>
        ))}

        {/* No Color (full-width row under the grid, as in the legacy selector) */}
        <ColorSwatchOption
          color={NO_COLOR}
          ariaLabel="No Color"
          className="col-span-4 h-9 w-full border border-gray-300 text-sm font-semibold text-gray-700 selected:bg-transparent selected:ring-2 selected:ring-gray-700"
        >
          No Color
        </ColorSwatchOption>
      </ColorSwatchPicker>
    </div>
  )
})
